import { useState } from 'react';
import { Box, Chip } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AdminTable from '../../shared/AdminTable';
import AuditLogDetailModal from './AuditLogDetailModal';
import { AdminAuditLogEntry, AdminTableColumn, AdminTableAction } from '../../../../types/admin';
import { formatDistanceToNow } from 'date-fns';

interface AuditLogTableProps {
  auditLog: AdminAuditLogEntry[];
  loading?: boolean;
}

export default function AuditLogTable({ auditLog, loading }: AuditLogTableProps) {
  const [selectedEntry, setSelectedEntry] = useState<AdminAuditLogEntry | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  const columns: AdminTableColumn<AdminAuditLogEntry>[] = [
    {
      key: 'admin_name',
      label: 'Admin',
      sortable: true,
      width: 180,
      render: (row) => (
        <Box>
          <Box sx={{ fontWeight: 500 }}>{row.admin_name}</Box>
          <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{row.admin_email}</Box>
        </Box>
      ),
    },
    {
      key: 'action_type',
      label: 'Action',
      sortable: true,
      width: 150,
      render: (row) => {
        const colorMap = {
          view: 'default' as const,
          create: 'success' as const,
          update: 'primary' as const,
          delete: 'error' as const,
          export: 'warning' as const,
          bulk_operation: 'secondary' as const,
          system_config: 'info' as const,
        };
        return (
          <Chip
            label={row.action_type.toUpperCase()}
            size="small"
            color={colorMap[row.action_type] || 'default'}
          />
        );
      },
    },
    {
      key: 'action_description',
      label: 'Description',
      sortable: false,
      render: (row) => (
        <Box sx={{ maxWidth: 350, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {row.action_description}
        </Box>
      ),
    },
    {
      key: 'entity_type',
      label: 'Entity',
      sortable: true,
      width: 120,
      render: (row) => (
        <Chip label={row.entity_type.toUpperCase()} size="small" variant="outlined" />
      ),
    },
    {
      key: 'success',
      label: 'Status',
      sortable: true,
      width: 100,
      render: (row) => (
        <Chip
          label={row.success ? 'SUCCESS' : 'FAILED'}
          size="small"
          color={row.success ? 'success' : 'error'}
        />
      ),
    },
    {
      key: 'execution_time_ms',
      label: 'Time',
      sortable: true,
      width: 100,
      render: (row) => `${row.execution_time_ms}ms`,
    },
    {
      key: 'created_at',
      label: 'Timestamp',
      sortable: true,
      width: 150,
      render: (row) => formatDistanceToNow(new Date(row.created_at), { addSuffix: true }),
    },
  ];

  const actions: AdminTableAction<AdminAuditLogEntry>[] = [
    {
      label: 'View Details',
      icon: <VisibilityIcon />,
      onClick: (row) => {
        setSelectedEntry(row);
        setDetailModalOpen(true);
      },
      color: 'primary',
    },
  ];

  return (
    <>
      <AdminTable columns={columns} data={auditLog} actions={actions} isLoading={loading} />

      {selectedEntry && (
        <AuditLogDetailModal
          open={detailModalOpen}
          entry={selectedEntry}
          onClose={() => setDetailModalOpen(false)}
        />
      )}
    </>
  );
}
